import React, { useState, useEffect } from "react";
import { ThemeProvider, CssBaseline, PaletteMode } from "@mui/material";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import theme from "./theme"
import StartChat from "./feature-chat/Chat";
import Header from "./shared-components/header/Header";
import LoginPage from "./feature-auth/login/Login";
import RegisterPage from "./feature-auth/register/Register";
import { AuthProvider, useAuth } from "./feature-auth/context/AuthContext";
import { LoadingSpinner } from "./shared-components/loading/LoadingSpinner";

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const App = () => {
  const [mode, setMode] = useState<PaletteMode>("dark");
  const { user } = useAuth();
  const toggleMode = () => {
    setMode((prevMode: PaletteMode) => (prevMode === "light" ? "dark" : "light"));
    localStorage.setItem("theme", mode === "light" ? "dark" : "light");
  };

  useEffect(() => {
    const savedMode: string | null = localStorage.getItem("theme");
    if (savedMode) {
      setMode(savedMode as "light" | "dark");
    } else {
      const prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
      setMode(prefersDark ? "dark" : "light");
    }

    // Add listener for system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem("theme")) {
        setMode(e.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);

    // Cleanup listener on component unmount
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return (
    <ThemeProvider theme={theme(mode)}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          {user && <Header mode={mode} toggleMode={toggleMode} />}
          <Routes>
            <Route
              path="/login"
              element={
                user ? <Navigate to="/chat" replace /> : <LoginPage />
              }
            />
            <Route
              path="/register"
              element={
                user ? <Navigate to="/chat" replace /> : <RegisterPage />
              }
            />
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <StartChat />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/login" replace />} />
          </Routes>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;